import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  categoryDetails,
  difficulties,
  matchesQuestion,
  questionTypes,
  questions,
} from '../data/questions';

const typeLabels = {
  Any: 'Any type',
  multiple: 'Multiple choice',
  'true/false': 'True / false',
};

export default function StartScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('Any');
  const [type, setType] = useState('Any');

  const filteredQuestions = useMemo(() => {
    return questions.filter((question) =>
      matchesQuestion(question, {
        search,
        difficulty,
        type,
      })
    );
  }, [difficulty, search, type]);

  const cards = useMemo(() => {
    return categoryDetails.map((category) => {
      const matchingQuestions = filteredQuestions.filter((question) => question.category === category.name);
      const difficultiesAvailable = Array.from(
        new Set(matchingQuestions.map((question) => question.difficulty))
      );
      const sampleTags = Array.from(
        new Set(matchingQuestions.flatMap((question) => question.tags || []))
      ).slice(0, 4);

      return {
        ...category,
        count: matchingQuestions.length,
        difficultiesAvailable,
        sampleTags,
      };
    });
  }, [filteredQuestions]);

  const visibleCards = cards.filter((card) => card.count > 0);
  const hasActiveFilters = search || difficulty !== 'Any' || type !== 'Any';

  const startQuiz = (category = 'All') => {
    navigate('/quiz', {
      state: {
        category,
        difficulty,
        type,
        search,
      },
    });
  };

  const clearFilters = () => {
    setSearch('');
    setDifficulty('Any');
    setType('Any');
  };

  return (
    <section className="browse-screen">
      <div className="browse-hero">
        <div>
          <p className="eyebrow">Tiny trivia adventure</p>
          <h1 className="title is-1">QuizGarden</h1>
          <p className="subtitle is-5">
            Search the question garden, pick a topic, and see what your brain has been hoarding.
          </p>
        </div>
        <div className="hero-score-card">
          <span>{filteredQuestions.length}</span>
          <strong>matching questions</strong>
          <button
            className="button is-primary is-fullwidth mt-3"
            type="button"
            onClick={() => startQuiz('All')}
            disabled={filteredQuestions.length === 0}
          >
            Shuffle All Matches
          </button>
        </div>
      </div>

      <div className="quiz-card browse-controls">
        <div className="field search-field">
          <label className="label" htmlFor="quiz-search">
            Search topics
          </label>
          <div className="control">
            <input
              id="quiz-search"
              className="input"
              type="search"
              placeholder="Try space, Shakespeare, Easy, true/false..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="filter-strip" aria-label="Difficulty filters">
          {difficulties.map((item) => (
            <button
              key={item}
              className={`chip-button ${difficulty === item ? 'is-selected' : ''}`}
              type="button"
              onClick={() => setDifficulty(item)}
            >
              {item === 'Any' ? 'Any difficulty' : item}
            </button>
          ))}
        </div>

        <div className="filter-strip" aria-label="Question type filters">
          {questionTypes.map((item) => (
            <button
              key={item}
              className={`chip-button ${type === item ? 'is-selected' : ''}`}
              type="button"
              onClick={() => setType(item)}
            >
              {typeLabels[item]}
            </button>
          ))}
        </div>

        {hasActiveFilters && (
          <button className="button is-light is-small" type="button" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {visibleCards.length === 0 ? (
        <div className="quiz-card empty-state browse-empty">
          <span className="empty-icon">🪄</span>
          <h2 className="title is-4">No quiz sprouts yet</h2>
          <p>Try a broader search or clear a filter to bring the cards back.</p>
          <button className="button is-primary mt-4" type="button" onClick={clearFilters}>
            Reset Browse
          </button>
        </div>
      ) : (
        <div className="category-grid">
          {visibleCards.map((card) => (
            <article className="category-card" key={card.name}>
              <div className="category-card-top">
                <span className="category-icon" aria-hidden="true">
                  {card.icon}
                </span>
                <span className="question-count">{card.count} questions</span>
              </div>
              <h2>{card.name}</h2>
              <p>{card.description}</p>
              <div className="badge-row">
                {card.difficultiesAvailable.map((item) => (
                  <span className={`badge badge--${item.toLowerCase()}`} key={item}>
                    {item}
                  </span>
                ))}
              </div>
              {card.sampleTags.length > 0 && (
                <div className="tag-row">
                  {card.sampleTags.map((tag) => (
                    <span key={tag}>#{tag}</span>
                  ))}
                </div>
              )}
              <button
                className="button is-primary is-fullwidth"
                type="button"
                onClick={() => startQuiz(card.name)}
              >
                Start {card.icon}
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
